/**
 * Mensajes de la UI de react-admin en español (equivalente a ra-language-french / ra-language-english).
 *
 * Va en el repo en vez de como dependencia porque el único paquete mantenido para react-admin 3.x,
 * @blackbox-vision/ra-language-spanish, solo publica builds ESM (sin campo `main`) y jest no lo
 * resuelve. Partiendo de esa base, el texto se ajustó al español latinoamericano que usa es.ts:
 * «Agregar» en vez de «Añadir», «Eliminar» en vez de «Borrar», «Actualizar» en vez de «Refrescar»,
 * «elementos» en vez de «items», y «Panel» para el dashboard, igual que app.dashboard.title.
 */
const raSpanishMessages = {
    ra: {
        action: {
            add: 'Agregar',
            add_filter: 'Agregar filtro',
            back: 'Volver',
            bulk_actions: '1 elemento seleccionado |||| %{smart_count} elementos seleccionados',
            cancel: 'Cancelar',
            clear_input_value: 'Limpiar valor',
            clone: 'Clonar',
            close: 'Cerrar',
            close_menu: 'Cerrar menú',
            confirm: 'Confirmar',
            create: 'Crear',
            create_item: 'Crear %{item}',
            delete: 'Eliminar',
            edit: 'Editar',
            expand: 'Expandir',
            export: 'Exportar',
            list: 'Listar',
            move_up: 'Mover arriba',
            move_down: 'Mover abajo',
            open_menu: 'Abrir menú',
            refresh: 'Actualizar',
            remove: 'Eliminar',
            remove_filter: 'Quitar filtro',
            save: 'Guardar',
            search: 'Buscar',
            select_all: 'Seleccionar todo',
            select_row: 'Seleccionar esta fila',
            show: 'Mostrar',
            sort: 'Ordenar',
            undo: 'Deshacer',
            unselect: 'Deseleccionar',
            update: 'Actualizar',
        },
        auth: {
            auth_check_error: 'Inicie sesión para continuar',
            logout: 'Cerrar sesión',
            password: 'Contraseña',
            sign_in: 'Iniciar sesión',
            sign_in_error: 'La autenticación falló, vuelva a intentarlo',
            user_menu: 'Perfil',
            username: 'Usuario',
        },
        boolean: {
            true: 'Sí',
            false: 'No',
            null: ' ',
        },
        input: {
            file: {
                upload_several: 'Arrastre algunos archivos para cargar o haga clic para seleccionarlos.',
                upload_single: 'Arrastre un archivo para cargar o haga clic para seleccionarlo.',
            },
            image: {
                upload_several: 'Arrastre algunas imágenes para cargar o haga clic para seleccionarlas.',
                upload_single: 'Arrastre alguna imagen para cargar o haga clic para seleccionarla.',
            },
            references: {
                all_missing: 'No se pueden encontrar datos de referencias.',
                many_missing: 'Al menos una de las referencias asociadas parece no estar disponible.',
                single_missing: 'La referencia asociada no parece estar disponible.',
            },
            password: {
                toggle_visible: 'Ocultar contraseña',
                toggle_hidden: 'Mostrar contraseña',
            },
        },
        message: {
            about: 'Acerca de',
            are_you_sure: '¿Está seguro?',
            bulk_delete_content:
                '¿Seguro que quiere eliminar este %{name}? |||| ¿Seguro que quiere eliminar estos %{smart_count} elementos?',
            bulk_delete_title: 'Eliminar %{name} |||| Eliminar %{smart_count} elementos de %{name}',
            bulk_update_content:
                '¿Seguro que quiere actualizar este %{name}? |||| ¿Seguro que quiere actualizar estos %{smart_count} elementos?',
            bulk_update_title: 'Actualizar %{name} |||| Actualizar %{smart_count} elementos de %{name}',
            delete_content: '¿Seguro que quiere eliminar este elemento?',
            delete_title: 'Eliminar %{name} #%{id}',
            details: 'Detalles',
            error: 'Se produjo un error en el cliente y su solicitud no se pudo completar',
            invalid_form: 'El formulario no es válido. Verifique si hay errores',
            loading: 'La página se está cargando, espere un momento por favor',
            no: 'No',
            not_found: 'Escribió una URL incorrecta o siguió un enlace incorrecto.',
            yes: 'Sí',
            unsaved_changes: 'Algunos de sus cambios no se guardaron. ¿Seguro que quiere descartarlos?',
        },
        navigation: {
            next: 'Siguiente',
            no_more_results: 'El número de página %{page} está fuera de los límites. Pruebe la página anterior.',
            no_results: 'No se encontraron resultados',
            page_out_from_begin: 'No puede ir antes de la página 1',
            page_out_from_end: 'No puede ir después de la última página',
            page_out_of_boundaries: 'Número de página %{page} fuera de los límites',
            page_range_info: '%{offsetBegin} - %{offsetEnd} de %{total}',
            page_rows_per_page: 'Filas por página:',
            prev: 'Anterior',
            skip_nav: 'Saltar al contenido',
        },
        sort: {
            sort_by: 'Ordenar por %{field} %{order}',
            ASC: 'ascendente',
            DESC: 'descendente',
        },
        notification: {
            bad_item: 'Elemento incorrecto',
            canceled: 'Acción cancelada',
            created: 'Elemento creado',
            data_provider_error: 'Error del proveedor de datos. Consulte la consola para más detalles.',
            deleted: 'Elemento eliminado |||| %{smart_count} elementos eliminados.',
            http_error: 'Error de comunicación con el servidor',
            item_doesnt_exist: 'El elemento no existe',
            logged_out: 'Su sesión finalizó, vuelva a conectarse.',
            updated: 'Elemento actualizado |||| %{smart_count} elementos actualizados',
            i18n_error: 'No se pudieron cargar las traducciones para el idioma especificado',
            not_authorized: 'No tiene autorización para acceder a este recurso.',
        },
        page: {
            create: 'Crear %{name}',
            dashboard: 'Panel',
            edit: '%{name} #%{id}',
            empty: 'Sin %{name} todavía.',
            error: 'Algo salió mal',
            invite: '¿Quiere agregar uno?',
            list: 'Lista de %{name}',
            loading: 'Cargando',
            not_found: 'No encontrado',
            show: '%{name} #%{id}',
        },
        validation: {
            email: 'Debe ser un correo electrónico válido',
            maxLength: 'Debe contener %{max} caracteres o menos',
            maxValue: 'Debe ser %{max} o menos',
            minLength: 'Debe contener al menos %{min} caracteres',
            minValue: 'Debe ser al menos %{min}',
            number: 'Debe ser un número',
            oneOf: 'Debe ser uno de: %{options}',
            regex: 'Debe coincidir con un formato específico (regexp): %{pattern}',
            required: 'Campo obligatorio',
        },
    },
};

export default raSpanishMessages;
